import { useState } from 'react';
import { ArrowUpCircle } from 'lucide-react';
import { ConfirmModal } from '@/components/ui/ConfirmModal';

interface ModelUpgradeBannerProps {
  currentVersion: string;
  latestVersion: string;
  /** Fires after the user confirms the in-place upgrade. */
  onUpgrade: () => void;
  upgrading?: boolean;
  /** True when this conversation has an accepted-for-underwriting version. */
  accepted?: boolean;
}

/**
 * "A newer model version is available — Update to vN" banner. Unlike the old
 * fork flow, the update is IN PLACE: the conversation + chat history are kept;
 * inputs no longer in the new model move to the orphaned list. Gated behind a
 * confirm (with a note for accepted-for-underwriting deals).
 */
export function ModelUpgradeBanner({
  currentVersion,
  latestVersion,
  onUpgrade,
  upgrading = false,
  accepted = false,
}: ModelUpgradeBannerProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  if (currentVersion === latestVersion) return null;

  return (
    <>
      <div
        data-testid="model-upgrade-banner"
        className="px-6 py-3 bg-warn-dim border-b border-warn/10 flex items-center justify-between shrink-0"
      >
        <div className="flex items-center gap-2">
          <ArrowUpCircle size={14} className="text-warn shrink-0" />
          <span className="text-sm font-body text-text">
            A newer version{' '}
            <span className="font-mono text-text-bright">v{latestVersion}</span>{' '}
            of this model is available.
          </span>
        </div>
        <button
          onClick={() => setConfirmOpen(true)}
          disabled={upgrading}
          data-testid="model-upgrade-button"
          className="py-2 px-4 bg-accent-dim border border-accent/15 rounded-[3px] text-xs font-mono font-semibold text-accent cursor-pointer transition-all duration-200 hover:border-accent/27 hover:bg-accent/13 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {upgrading ? 'Updating…' : `Update to v${latestVersion}`}
        </button>
      </div>

      <ConfirmModal
        open={confirmOpen}
        title={`Update to model v${latestVersion}`}
        confirmLabel="Update"
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false);
          onUpgrade();
        }}
      >
        <p className="text-sm text-text-muted font-body leading-relaxed">
          This deal moves to the latest model version. Your chat history and
          current inputs are kept; any inputs no longer in the new model move to
          the &ldquo;no longer in this model version&rdquo; list.
        </p>
        {accepted && (
          <p className="mt-2 text-xs text-warn font-body leading-relaxed">
            This deal has a version accepted for underwriting on the current
            model. Updating won&rsquo;t change that record, but you may want to
            re-accept on the new version afterward.
          </p>
        )}
      </ConfirmModal>
    </>
  );
}
